import { cn } from '@/lib/utils';
import { formatRelativeAgeLabel } from '@/shared/lib';
import { Badge } from '@/shared/ui/shadcn/badge';

import { POLICY_RELATIONSHIP_LABEL, formatPatientLocationLabel } from '../model';
import type { PatientNavigatorItemData } from '../model';

interface PatientNavigatorItemProps {
  patient: PatientNavigatorItemData;
  isActive: boolean;
  onSelect: () => void;
}

const RELATIONSHIP_STYLE: Record<PatientNavigatorItemData['policyRelationship'], string> = {
  actionable: 'border-green-500/40 bg-green-500/10 text-green-600 dark:text-green-400',
  licensed: 'border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400',
  unlicensed: 'border-orange-500/40 bg-orange-500/10 text-orange-600 dark:text-orange-400',
};

export function PatientNavigatorItem({ patient, isActive, onSelect }: PatientNavigatorItemProps) {
  const showUrgentCount = patient.urgentCount > 0;
  const showRoutineCount = patient.routineCount > 0;
  const showUnreadCount = patient.unreadCount > 0;
  const showActiveVisit = patient.viewerCount > 0;

  const ageLabel = formatRelativeAgeLabel(patient.oldestRequestAt);
  const relationshipClassName = RELATIONSHIP_STYLE[patient.policyRelationship];
  const relationshipLabel = POLICY_RELATIONSHIP_LABEL[patient.policyRelationship];
  const patientLocationLabel = formatPatientLocationLabel(patient.patientCurrentState);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full rounded-lg px-2.5 py-2 text-left transition-colors',
        'hover:bg-sidebar-accent',
        isActive && 'bg-sidebar-accent'
      )}
    >
      {/* Row 1: Name + active visit + count badges */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-xs font-semibold text-sidebar-foreground">
            {patient.fullName}
          </span>
          {showActiveVisit ? (
            <span
              className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-green-500"
              aria-label="Visit in progress"
            />
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {showUnreadCount ? (
            <Badge
              variant="secondary"
              className="h-4 min-w-4 rounded-full bg-unread-badge px-1 text-[10px] text-white"
              aria-label={`${patient.unreadCount} unread`}
            >
              {patient.unreadCount}
            </Badge>
          ) : null}
          {showUrgentCount ? (
            <Badge
              variant="destructive"
              className="h-4 min-w-4 rounded-full bg-urgent-badge px-1 text-[10px] text-white"
              aria-label={`${patient.urgentCount} urgent`}
            >
              {patient.urgentCount}
            </Badge>
          ) : null}
          {showRoutineCount ? (
            <Badge
              variant="secondary"
              className="h-4 min-w-4 rounded-full bg-routine-badge px-1 text-[10px] text-white"
              aria-label={`${patient.routineCount} routine`}
            >
              {patient.routineCount}
            </Badge>
          ) : null}
        </div>
      </div>

      {/* Row 2: Policy chip + patient state + practice areas + age */}
      <div className="mt-1 flex items-center gap-1.5 overflow-hidden">
        <Badge
          variant="outline"
          className={cn(
            'h-4 shrink-0 rounded-full px-1.5 text-[10px] font-medium leading-none',
            relationshipClassName
          )}
        >
          {relationshipLabel}
        </Badge>

        <span className="truncate text-[10px] font-medium text-muted-foreground">
          {patientLocationLabel}
          {patient.patientPracticeAreas.length > 0
            ? ` · ${patient.patientPracticeAreas.join(', ')}`
            : null}
        </span>

        {ageLabel ? (
          <span className="ml-auto shrink-0 whitespace-nowrap text-[10px] font-medium text-muted-foreground/60">
            {ageLabel}
          </span>
        ) : null}
      </div>
    </button>
  );
}
